import { useMemo } from "react";
import { Expansion } from "../types/common/Expansion";
import { Map } from "../types/Map";
import { MapType } from "../types/common/MapType";

export default function useGameFilterMaps(
    maps: Map[],
    selectedExpansions: string[],
    selectedMapTypes: string[]
) {
    return useMemo(() => {
        let filteredMaps = maps;

        // Convert strings to proper enum types
        const expansions = selectedExpansions as Expansion[];
        const mapTypes = selectedMapTypes as MapType[];

        // Apply expansion filtering with special logic for world maps
        if (expansions.length > 0) {
            filteredMaps = maps.filter((m) => {
                if (!m.expansion) return false;

                // Special logic for world maps
                if (m.type === MapType.WORLD_MAP) {
                    return true;
                } else {
                    // For non-world maps, include it if its expansion or a next one is selected. Order is ARR < HW < SB < ShB < EW < DT
                    const expansionIndex: Record<Expansion, number> =
                        Object.values(Expansion).reduce((acc, exp, index) => {
                            acc[exp] = index;
                            return acc;
                        }, {} as Record<Expansion, number>);

                    const selectedExpansionIndices = expansions
                        .map(exp => expansionIndex[exp]);

                    const maxSelectedIndex = Math.max(...selectedExpansionIndices);

                    const mapExpansionIndex = expansionIndex[m.expansion];
                    if (mapExpansionIndex === undefined) {
                        return false;
                    }

                    return mapExpansionIndex <= maxSelectedIndex;

                }
            });
        }

        // Apply map type filtering - always include WORLD_MAP, REGION, MAP, and filter others
        filteredMaps = filteredMaps.filter((m) => {
            if (!m.type) return false;

            // Always show these core map types
            if ([MapType.WORLD_MAP, MapType.REGION, MapType.MAP].includes(m.type)) {
                return true;
            }

            // For other types (like DUNGEON), only show if explicitly selected
            return mapTypes.includes(m.type);
        });

        // Sort by map type priority - WORLD_MAP should always be first
        return filteredMaps.sort((a, b) => {
            const typeOrder = [
                MapType.WORLD_MAP,
                MapType.REGION,
                MapType.MAP,
                MapType.DUNGEON,
                MapType.TRIAL,
                MapType.RAID8,
                MapType.RAID24,
                MapType.OTHER
            ];
            const aTypeIndex = a.type ? typeOrder.indexOf(a.type) : typeOrder.length;
            const bTypeIndex = b.type ? typeOrder.indexOf(b.type) : typeOrder.length;
            return aTypeIndex - bTypeIndex;
        });
    }, [maps, selectedExpansions, selectedMapTypes]);
}